import React from "react";
import DatePicker from "react-datepicker";
import styles from "./dateTime.module.css";
import Select_ from "../Select";

const DatePicker_ = ({ id, label, key, required = false, ...datePickerProps }) => {
  // Generate time options with 15-minute intervals in 12-hour format
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hour12 = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const display = `${hour12.toString().padStart(2, '0')}:${minuteStr} ${ampm}`;
        options.push({
          label: display,
          value: `${hourStr}:${minuteStr}` // 24-hour format for backend
        });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Helper function to round minutes to nearest 15-minute interval
  const roundToNearest15 = (minutes) => {
    const numMinutes = parseInt(minutes) || 0;
    return (Math.round(numMinutes / 15) * 15) % 60;
  };

  // Get current selected time value from props
  const getSelectedTimeValue = () => {
    const hoursProp = datePickerProps.selectedHours;
    const minutesProp = datePickerProps.selectedMinutes;

    const hours = hoursProp?.value ?? hoursProp?.label ?? hoursProp;
    const minutes = minutesProp?.value ?? minutesProp?.label ?? minutesProp;
    
    if (hours === undefined || hours === null || hours === '') {
      return undefined;
    }
    
    // Parse values
    const parsedHours = parseInt(hours);
    const parsedMinutes = parseInt(minutes) || 0;
    
    if (isNaN(parsedHours)) {
      return undefined;
    }

    const validHours = Math.max(0, Math.min(23, parsedHours));
    const roundedMinutes = roundToNearest15(parsedMinutes);
    
    const hourStr = validHours.toString().padStart(2, '0');
    const minuteStr = roundedMinutes.toString().padStart(2, '0');
    
    return `${hourStr}:${minuteStr}`;
  };

  const selectedTime = getSelectedTimeValue();

  // Find the matching option for current selection
  const currentTimeOption = selectedTime 
    ? timeOptions.find(opt => opt.value === selectedTime) 
    : undefined;

  // Handle time selection
  const handleTimeChange = (selectedOption) => {
    if (!selectedOption) {
      if (datePickerProps.onHoursChange) {
        datePickerProps.onHoursChange({ label: '', value: '' });
      }
      if (datePickerProps.onMinutesChange) {
        datePickerProps.onMinutesChange({ label: '', value: '' });
      }
      return;
    }
    
    const [hours, minutes] = selectedOption.value.split(':');
    
    // Call both handlers to update parent state
    if (datePickerProps.onHoursChange) {
      datePickerProps.onHoursChange({ label: hours, value: hours });
    }
    if (datePickerProps.onMinutesChange) {
      datePickerProps.onMinutesChange({ label: minutes, value: minutes });
    }
  };

  const timeProps = {
    label: null,
    id: `${id}_time`,
    placeholder: "Select Time",
    type: "select",
    value: currentTimeOption,
    required: required,
    options: timeOptions,
    onChange: handleTimeChange,
    isClearable: !required,
    isSearchable: true,
    filterOption: () => true, // Don't filter based on input, just allow typing for focus
  };

  return (
    <div className={styles.formGroup} style={datePickerProps.formGroupStyleOverride} key={key}>
      <label htmlFor={id}>
        {label} {required && <span className="required">*</span>}
      </label>
      <div className={styles.dateTimePickerWrapperCustom}>
        <div className={styles.datePickerWrapper}>
          <DatePicker
            placeholderText="Choose Date"
            style={{ width: "40%" }}
            className={styles?.reactDatePickerCustom}
            required
            {...datePickerProps}
            minDate={datePickerProps.minDate}
            maxDate={datePickerProps.maxDate} 
          />
          <img src={"/icons/datePicker.svg"} />
        </div>

        <div className={styles.timePicker}>
          <Select_ {...timeProps} />
        </div>
      </div>
    </div>
  );
};

export default DatePicker_;
